import { fail } from './errors.js';
import type { Principal } from './rbac.js';

/*
 * "오늘 있었던 일" — 보호자 커뮤니티(맘카페형). 게시판은 두 종류.
 *  - 센터 게시판: 그 센터 아동과 연결된 보호자만 쓰고, 그 센터 직원은 읽기·관리(원장: 숨김·삭제).
 *  - 지역 게시판: 같은 지역(시·도 + 시·군·구) 센터들의 보호자가 함께 본다.
 * 글·댓글에는 닉네임과 센터 이름만 보이고 아동 이름은 넣지 않는다. 본문은 암호문으로만 저장한다(사진 첨부 없음).
 */
export const FEED_POST_MAX = 2000;
export const FEED_COMMENT_MAX = 500;
export const FEED_NICK_MAX = 12;
export const FEED_PAGE_SIZE = 50;
/** 이의제기 남용 방지: 한 사람이 24시간 안에 낼 수 있는 이의제기 수 */
export const FEED_OBJECTION_DAILY_LIMIT = 5;
export const OBJECTION_REASONS = { PERSONAL_INFO: '아동·가족 개인정보 노출', ABUSE: '욕설·비방', AD: '광고·홍보', FALSE_INFO: '잘못된 정보', OTHER: '기타' } as const;
export type ObjectionReason = keyof typeof OBJECTION_REASONS;
export const OBJECTION_TEXT_MAX = 300;

export const SIDO = ['서울특별시', '부산광역시', '대구광역시', '인천광역시', '광주광역시', '대전광역시', '울산광역시', '세종특별자치시', '경기도', '강원특별자치도', '충청북도', '충청남도', '전북특별자치도', '전라남도', '경상북도', '경상남도', '제주특별자치도'] as const;
export interface CenterRegion { sido: (typeof SIDO)[number]; sigungu: string }

export function validateRegion(r: { sido?: unknown; sigungu?: unknown }): CenterRegion {
  if (!SIDO.includes(r.sido as CenterRegion['sido'])) fail('VALIDATION', '시·도를 목록에서 고르세요.');
  const g = typeof r.sigungu === 'string' ? r.sigungu.trim() : '';
  if (!/^[가-힣]{1,10}(시|군|구)( [가-힣]{1,10}구)?$/.test(g)) fail('VALIDATION', '시·군·구를 확인하세요(예: 충주시, 수원시 영통구).');
  return { sido: r.sido as CenterRegion['sido'], sigungu: g };
}

export type FeedScope = 'CENTER' | 'REGION';
/** 저장 단위 키. 센터: CENTER#센터id, 지역: REGION#시도#시군구 */
export const centerBoard = (centerId: string) => `CENTER#${centerId}`;
export const regionBoard = (r: CenterRegion) => `REGION#${r.sido}#${r.sigungu}`;
export const regionLabel = (r: CenterRegion) => `${r.sido} ${r.sigungu}`;

export interface FeedPost {
  board: string; scope: FeedScope; id: string; centerId: string; authorId: string; nickname: string;
  bodyEnc: string; createdAt: string; version: number;
  /** NEWS = 지역 정보보고(시스템이 올림, 본문은 JSON) */
  kind?: 'NEWS';
  /** 감춤: MODERATOR = 원장이 감춤, OBJECTION = 이의제기 접수로 자동 감춤(검토 전) */
  hidden?: { by: string; at: string; why: 'MODERATOR' | 'OBJECTION' };
  deletedAt?: string; deletedBy?: string;
}
export interface FeedComment {
  board: string; postId: string; id: string; centerId: string; authorId: string; nickname: string;
  bodyEnc: string; createdAt: string; version: number;
  hidden?: { by: string; at: string; why: 'MODERATOR' | 'OBJECTION' };
  deletedAt?: string; deletedBy?: string;
}
/** 이의제기. 검토는 작성자 센터의 원장(지역 글도 작성자 센터). 사유 상세는 암호화. */
export interface FeedObjection {
  id: string; reviewCenterId: string; board: string; postId: string; commentId?: string;
  filedBy: string; reason: ObjectionReason; textEnc?: string; filedAt: string;
  status: 'OPEN' | 'RESTORED' | 'DELETED'; decidedAt?: string; decidedBy?: string; version: number;
}
/** 이의제기 중복 막기용 대상 키(글 또는 댓글) */
export const objectionTarget = (postId: string, commentId?: string) => (commentId ? `${postId}~${commentId}` : postId);
export const objectionAad = (o: Pick<FeedObjection, 'reviewCenterId' | 'id'>) => `feed-objection/${o.reviewCenterId}/${o.id}`;
export interface FeedReaction { board: string; postId: string; kind: 'LIKE' | 'REPORT'; userId: string; at: string }

export const feedPostAad = (board: string, postId: string) => `feed/${board}/${postId}`;
export const feedCommentAad = (board: string, postId: string, id: string) => `feed/${board}/${postId}/comments/${id}`;

export function validateFeedText(v: unknown, max: number): string {
  if (typeof v !== 'string') fail('VALIDATION', '내용을 입력하세요.');
  const t = (v as string).replace(/\r\n/g, '\n').trim();
  if (!t) fail('VALIDATION', '내용을 입력하세요.');
  if (t.length > max) fail('VALIDATION', `${max}자 이하로 입력하세요.`);
  return t;
}
export function validateNickname(v: unknown): string {
  const t = typeof v === 'string' ? v.trim() : '';
  if (t.length < 2 || t.length > FEED_NICK_MAX || !/^[가-힣a-zA-Z0-9_ ]+$/.test(t)) fail('VALIDATION', `닉네임은 2~${FEED_NICK_MAX}자 한글·영문·숫자로 입력하세요.`);
  return t;
}

/** 글·댓글·좋아요·신고: 보호자 본인만. 대리접속·자녀 모드·직원은 쓸 수 없다. */
export function requireFeedParticipant(p: Principal): void {
  if (p.impersonation) fail('FORBIDDEN', '대리접속 중에는 게시판에 글·댓글·좋아요·신고를 남길 수 없습니다.');
  if (p.childMode) fail('FORBIDDEN', '자녀 모드에서는 게시판을 쓸 수 없습니다.');
  if (p.role !== 'GUARDIAN') fail('FORBIDDEN', '보호자만 글·댓글·좋아요·신고를 남길 수 있습니다.');
}

/** 원장 관리 권한: 자기 센터 게시판만(대리접속 불가). */
export const isBoardModerator = (p: Principal, board: string) =>
  !p.impersonation && p.role === 'CENTER_DIRECTOR' && Boolean(p.centerId) && board === centerBoard(p.centerId!);

/** 이의제기: 참여 가능한 보호자, 자기 글 제외 */
export function requireCanObject(p: Principal, item: { authorId: string }): void {
  requireFeedParticipant(p);
  if (item.authorId === p.userId) fail('FORBIDDEN', '내 글에는 이의제기할 수 없습니다.');
}

export const canDeleteFeedItem = (p: Principal, item: { authorId: string; board: string }) =>
  !p.impersonation && !p.childMode && (item.authorId === p.userId || isBoardModerator(p, item.board));
